import { switchClasses } from '@mui/material/Switch';
import type { Components, Theme } from '@mui/material/styles';
import { varAlpha } from 'minimal-shared/utils';

// ----------------------------------------------------------------------

const MuiSwitch: Components<Theme>['MuiSwitch'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { color: 'default' },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: { alignItems: 'center' },
    switchBase: ({ ownerState, theme }) => ({
      top: 'unset',
      transform: 'translateX(6px)',
      [`&.${switchClasses.checked}`]: {
        // Default (uncolored) switches use the brand primary track with a white thumb when checked,
        // so toggles across the app read as branded rather than a monochrome on/off.
        [`&+.${switchClasses.track}`]: {
          opacity: 1,
          ...(ownerState.color === 'default' && {
            backgroundColor: theme.vars.palette.primary.main,
          }),
        },
      },
      [`&.${switchClasses.disabled}`]: {
        [`& .${switchClasses.thumb}`]: {
          opacity: 1,
          ...theme.applyStyles('dark', {
            opacity: 0.48,
          }),
        },
        [`&+.${switchClasses.track}`]: { opacity: 0.48 },
      },
    }),
    track: ({ theme }) => ({
      opacity: 1,
      borderRadius: 10,
      backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.48),
    }),
    thumb: ({ theme }) => ({ color: theme.vars.palette.common.white }),
    // A longer track with a capsule thumb, matching the current iOS switch rather than the shorter,
    // round-thumbed one it replaced. Both sizes keep a 3px inset all round, and the track and thumb
    // grow by the same amount, so MUI's own checked `translateX` still lands the thumb flush right.
    sizeMedium: {
      width: 68,
      [`& .${switchClasses.track}`]: { height: 20 },
      [`& .${switchClasses.thumb}`]: { width: 24, height: 14, borderRadius: 7 },
    },
    sizeSmall: {
      width: 48,
      [`& .${switchClasses.track}`]: { height: 16 },
      [`& .${switchClasses.thumb}`]: { width: 18, height: 10, borderRadius: 5 },
    },
  },
};

// ----------------------------------------------------------------------

export const switches = { MuiSwitch };
